import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Stack, Paper, Chip, Tabs, Tab, Button,
  CircularProgress, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, IconButton, Tooltip, MenuItem,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, FormControl, InputLabel, LinearProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Upload as UploadIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  ReportProblem as DiscrepancyIcon,
  RemoveRedEye as EyeIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bgvApi } from '../../../api/bgvApi';
import {
  BgvCaseDetailDto,
  BgvDocumentDto,
  BgvCaseStatus,
  BgvCaseStatusLabels,
  BgvCaseStatusColors,
  BgvFinalDecision,
  BgvFinalDecisionLabels,
  BgvFinalDecisionColors,
  BgvTaskStatus,
  BgvTaskStatusLabels,
  BgvTaskStatusColors,
  BgvDocumentStatus,
  BgvDocumentStatusLabels,
  BgvDiscrepancySeverity,
  BgvDiscrepancySeverityLabels,
  BgvDiscrepancySeverityColors,
  BgvDiscrepancyStatusLabels,
  BgvDiscrepancyStatus,
} from '../../../types/bgvTypes';
import { RootState } from '../../../store/store';
import { getUploadsBaseUrl } from '../../../config/runtimeConfig';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const verifiedBlockedMessage = 'All discrepancies must be resolved before marking this task as Verified.';
const finalizedTaskMessage = 'Task status is already finalized and cannot be changed.';
const similarDiscrepancyMessage = 'Similar discrepancy already exists for this task.';

const isTaskStatusFinalized = (status: BgvTaskStatus) =>
  status === BgvTaskStatus.Verified || status === BgvTaskStatus.Rejected;

const getApiErrorMessage = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  return data?.message || data?.title || fallback;
};

const BgvVerificationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roles = useSelector((state: RootState) => state.auth.user?.roles ?? []);
  const permissions = useSelector((state: RootState) => state.auth.user?.permissions ?? []);
  const isBgvVendorRole = roles.some((role) => role.toLowerCase() === 'bgvvendor');
  const isTenantAdmin = roles.some((role) => {
    const normalizedRole = role.toLowerCase();
    return normalizedRole.includes('admin') && normalizedRole !== 'superadmin';
  });
  const canManageBgv = (permissions.includes('ManageBGV') || isTenantAdmin) && !isBgvVendorRole;
  const isVendorPortal = permissions.includes('ViewAssignedBGV') && !canManageBgv;
  const [caseDetail, setCaseDetail] = useState<BgvCaseDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [discrepancyDialogOpen, setDiscrepancyDialogOpen] = useState(false);
  const [isDiscrepancyTaskLocked, setIsDiscrepancyTaskLocked] = useState(false);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [vendorDecisionDialogOpen, setVendorDecisionDialogOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState<BgvFinalDecision>(BgvFinalDecision.Clear);
  const [decisionRemarks, setDecisionRemarks] = useState('');
  const [discrepancyForm, setDiscrepancyForm] = useState({
    taskId: '',
    description: '',
    severity: BgvDiscrepancySeverity.Minor,
    hrRemarks: '',
  });
  const [isSubmittingDiscrepancy, setIsSubmittingDiscrepancy] = useState(false);
  const discrepancySubmissionInFlight = useRef(false);
  const [discrepancyError, setDiscrepancyError] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const loadCase = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = isVendorPortal
        ? await bgvApi.getAssignedCaseById(id)
        : await bgvApi.getCaseById(id);
      setCaseDetail(data);
    } catch {
      toast.error('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isVendorPortal]);

  const taskHasUnresolvedDiscrepancies = (taskId: string) =>
    caseDetail?.discrepancies.some(
      (discrepancy) =>
        discrepancy.taskId === taskId &&
        discrepancy.status !== BgvDiscrepancyStatus.Resolved
    ) ?? false;

  const taskHasAnyDiscrepancy = (taskId: string) =>
    caseDetail?.discrepancies.some((discrepancy) => discrepancy.taskId === taskId) ?? false;

  const canReportTaskDiscrepancy = (task: { id: string; status: BgvTaskStatus }) =>
    !isTaskStatusFinalized(task.status) && !taskHasAnyDiscrepancy(task.id);

  const taskDiscrepancyBlockedMessage = (task: { id: string; status: BgvTaskStatus }) => {
    if (isTaskStatusFinalized(task.status)) {
      return 'Discrepancies cannot be reported for verified or rejected tasks.';
    }

    if (taskHasAnyDiscrepancy(task.id)) {
      return 'A discrepancy has already been reported for this task.';
    }

    return 'Report Discrepancy';
  };

  const getVerifyTaskTooltip = (taskId: string) =>
    taskHasUnresolvedDiscrepancies(taskId) ? verifiedBlockedMessage : 'Mark Verified';

  const handleTaskStatusUpdate = async (taskId: string, status: BgvTaskStatus) => {
    const task = caseDetail?.tasks.find((item) => item.id === taskId);
    if (task && isTaskStatusFinalized(task.status)) {
      toast.error(finalizedTaskMessage);
      return;
    }

    if (status === BgvTaskStatus.Verified && taskHasUnresolvedDiscrepancies(taskId)) {
      toast.error(verifiedBlockedMessage);
      setTabValue(2);
      return;
    }

    try {
      if (isVendorPortal) {
        await bgvApi.updateVendorTaskStatus(taskId, status);
      } else {
        await bgvApi.updateTaskStatus(taskId, status);
      }
      toast.success('Task status updated');
      await loadCase();
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Failed to update task status');
      toast.error(msg);
    }
  };

  const resetDiscrepancyForm = (taskId = '') => {
    setDiscrepancyForm({
      taskId,
      description: '',
      severity: BgvDiscrepancySeverity.Minor,
      hrRemarks: '',
    });
  };

  const openDiscrepancyDialog = (taskId = '', lockTask = false) => {
    if (taskId) {
      const task = caseDetail?.tasks.find((item) => item.id === taskId);
      if (task && !canReportTaskDiscrepancy(task)) {
        toast.error(taskDiscrepancyBlockedMessage(task));
        return;
      }
    }

    setDiscrepancyError(null);
    resetDiscrepancyForm(taskId);
    setIsDiscrepancyTaskLocked(lockTask);
    setDiscrepancyDialogOpen(true);
  };

  const closeDiscrepancyDialog = () => {
    if (isSubmittingDiscrepancy) return;
    setDiscrepancyDialogOpen(false);
    setIsDiscrepancyTaskLocked(false);
    setDiscrepancyError(null);
    resetDiscrepancyForm();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTaskId) return;
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    try {
      if (isVendorPortal) {
        await bgvApi.vendorUploadDocument(selectedTaskId, file);
      } else {
        await bgvApi.uploadDocument(selectedTaskId, file);
      }
      toast.success('Document uploaded');
      setUploadDialogOpen(false);
      await loadCase();
    } catch {
      toast.error('Failed to upload document');
    }
  };

  const handleDocumentReview = async (documentId: string, status: BgvDocumentStatus) => {
    try {
      await bgvApi.updateDocumentStatus(documentId, status);
      toast.success('Document status updated');
      await loadCase();
    } catch {
      toast.error('Failed to update document');
    }
  };

  const handleCreateDiscrepancy = async () => {
    const description = discrepancyForm.description.trim();
    if (!caseDetail || !description || isSubmittingDiscrepancy || discrepancySubmissionInFlight.current) return;
    setDiscrepancyError(null);
    if (isVendorPortal && !discrepancyForm.taskId) {
      setDiscrepancyError('Select a task before reporting a discrepancy.');
      return;
    }

    const hasSimilarDiscrepancy = caseDetail.discrepancies.some(
      (discrepancy) =>
        (discrepancy.taskId || '') === discrepancyForm.taskId &&
        discrepancy.description.trim().toLowerCase() === description.toLowerCase()
    );
    if (hasSimilarDiscrepancy) {
      setDiscrepancyError(similarDiscrepancyMessage);
      return;
    }

    if (discrepancyForm.taskId) {
      const task = caseDetail.tasks.find((item) => item.id === discrepancyForm.taskId);
      if (task && !canReportTaskDiscrepancy(task)) {
        setDiscrepancyError(taskDiscrepancyBlockedMessage(task));
        return;
      }
    }

    discrepancySubmissionInFlight.current = true;
    setIsSubmittingDiscrepancy(true);
    try {
      const payload = {
        caseId: caseDetail.id,
        taskId: discrepancyForm.taskId || undefined,
        description,
        severity: discrepancyForm.severity,
        hrRemarks: discrepancyForm.hrRemarks || undefined,
      };

      if (isVendorPortal) {
        await bgvApi.createAssignedDiscrepancy(payload);
      } else {
        await bgvApi.createDiscrepancy(payload);
      }
      toast.success('Discrepancy reported');
      setDiscrepancyDialogOpen(false);
      setIsDiscrepancyTaskLocked(false);
      resetDiscrepancyForm();
      await loadCase();
      setTabValue(2);
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Failed to report discrepancy');
      setDiscrepancyError(msg);
      toast.error(msg);
    } finally {
      discrepancySubmissionInFlight.current = false;
      setIsSubmittingDiscrepancy(false);
    }
  };

  const handleResolveDiscrepancy = async (discrepancyId: string) => {
    try {
      await bgvApi.resolveDiscrepancy(discrepancyId);
      toast.success('Discrepancy resolved successfully');
      await loadCase();
    } catch {
      toast.error('Failed to resolve discrepancy');
    }
  };

  const handleFinalDecision = async () => {
    if (!caseDetail) return;

    const hasOpenDiscrepancies = caseDetail.discrepancies.some((d) => d.status !== BgvDiscrepancyStatus.Resolved);
    if (hasOpenDiscrepancies) {
      const msg = 'Please resolve all discrepancies before completing the task.';
      toast.error(msg);
      setDecisionDialogOpen(false);
      setTabValue(2);
      return;
    }

    try {
      await bgvApi.updateFinalDecision(caseDetail.id, selectedDecision, decisionRemarks || undefined);
      toast.success('Final decision updated');
      setDecisionDialogOpen(false);
      await loadCase();
    } catch {
      toast.error('Failed to update decision');
    }
  };

  const handleVendorDecisionSubmit = async () => {
    if (!caseDetail) return;
    try {
      await bgvApi.submitVendorDecision(caseDetail.id, selectedDecision, decisionRemarks || undefined);
      toast.success('Submitted for HR review');
      setVendorDecisionDialogOpen(false);
      await loadCase();
    } catch (err: any) {
      const msg = getApiErrorMessage(err, 'Failed to submit for HR review');
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!caseDetail) return null;

  const progress = caseDetail.totalTasks > 0
    ? (caseDetail.completedTasks / caseDetail.totalTasks) * 100
    : 0;

  const openDiscrepancyCount = caseDetail.discrepancies.filter((d) => d.status !== BgvDiscrepancyStatus.Resolved).length;
  const hasOpenDiscrepancies = openDiscrepancyCount > 0;
  const decisionBlockedMessage = 'Please resolve all discrepancies before completing the task.';
  const assignedToDisplayName = caseDetail.vendorName || caseDetail.assignedToName || '-';
  const reportableTasks = caseDetail.tasks.filter(canReportTaskDiscrepancy);
  const hasReportableTasks = reportableTasks.length > 0;

  type BgvDocumentWithInlineData = BgvDocumentDto & { base64?: string };

  const getDocumentUrl = (doc: BgvDocumentWithInlineData) => {
    if (doc.base64) {
      let mime = 'application/pdf';
      if (doc.fileName && /\.pdf$/i.test(doc.fileName)) mime = 'application/pdf';
      if (doc.fileName && /\.png$/i.test(doc.fileName)) mime = 'image/png';
      if (doc.fileName && /\.jpg|\.jpeg$/i.test(doc.fileName)) mime = 'image/jpeg';
      return `data:${mime};base64,${doc.base64}`;
    }

    const filePath = doc.filePath || (doc.fileName ? `uploads/bgv/${doc.fileName}` : '');
    if (!filePath) return '';
    if (/^https?:\/\//i.test(filePath)) return filePath;
    return `${getUploadsBaseUrl()}/${filePath.replace(/^\/+/, '')}`;
  };

  const openDocumentPreview = (doc: BgvDocumentDto) => {
    const url = getDocumentUrl(doc);
    if (!url) {
      toast.error('Attachment file path is not available');
      return;
    }

    setPreviewUrl(url);
    setPreviewDialogOpen(true);
  };

  const getDocumentDownloadName = (doc: BgvDocumentDto) => {
    if (doc.fileName?.trim()) return doc.fileName.trim();
    const filePath = doc.filePath || '';
    const lastSegment = filePath.split(/[\\/]/).filter(Boolean).pop();
    return lastSegment || 'bgv-attachment';
  };

  const canPreviewFile = (fileName: string) => {
    return /\.(pdf|jpg|jpeg|png)$/i.test(fileName);
  };

  const handleOpenDocument = (doc: BgvDocumentDto) => {
    const url = getDocumentUrl(doc);
    if (!url) {
      toast.error('Attachment file path is not available');
      return;
    }

    if (canPreviewFile(getDocumentDownloadName(doc))) {
      openDocumentPreview(doc);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = (doc: BgvDocumentDto) => {
    const url = getDocumentUrl(doc);
    if (!url) {
      toast.error('Attachment file path is not available');
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = getDocumentDownloadName(doc);
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const getPreviewDialogTitle = () => {
    if (!previewUrl) return 'Attachment Preview';
    if (/\.pdf($|\?)/i.test(previewUrl)) return 'PDF Preview';
    if (previewUrl.startsWith('data:application/pdf')) return 'PDF Preview';
    if (previewUrl.startsWith('data:image/') || /\.(jpg|jpeg|png)($|\?)/i.test(previewUrl)) return 'Image Preview';
    return 'Attachment Preview';
  };

  const selectedPreviewIsImage = Boolean(
    previewUrl &&
    (previewUrl.startsWith('data:image/') || /\.(jpg|jpeg|png)($|\?)/i.test(previewUrl))
  );

  const selectedPreviewIsPdf = Boolean(
    previewUrl &&
    (previewUrl.startsWith('data:application/pdf') || /\.pdf($|\?)/i.test(previewUrl))
  );

  const canRenderPreview = selectedPreviewIsImage || selectedPreviewIsPdf;

  const closePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setPreviewUrl(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={() => navigate(isVendorPortal ? '/bgv/assigned-cases' : '/bgv/cases')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>{caseDetail.caseNumber}</Typography>
          <Typography variant="body2" color="text.secondary">
            {caseDetail.employeeName} ({caseDetail.employeeCode})
          </Typography>
        </Box>
        <Chip label={BgvCaseStatusLabels[caseDetail.status]} color={BgvCaseStatusColors[caseDetail.status]} />
        <Chip label={BgvFinalDecisionLabels[caseDetail.finalDecision]} color={BgvFinalDecisionColors[caseDetail.finalDecision]} variant="outlined" />
        {isVendorPortal && caseDetail.status !== BgvCaseStatus.Completed && caseDetail.status !== BgvCaseStatus.Failed && (
          <Button variant="contained" onClick={() => setVendorDecisionDialogOpen(true)}>
            Submit for HR Review
          </Button>
        )}
        {canManageBgv && caseDetail.status !== BgvCaseStatus.Completed && (
          <Tooltip title={hasOpenDiscrepancies ? decisionBlockedMessage : 'Set final decision'}>
            <span>
              <Button
                variant="contained"
                disabled={hasOpenDiscrepancies}
                onClick={() => {
                  if (hasOpenDiscrepancies) {
                    toast.error(decisionBlockedMessage);
                    setTabValue(2);
                    return;
                  }
                  setDecisionDialogOpen(true);
                }}
              >
                Set Decision
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography variant="body2">Task Completion</Typography>
          <Typography variant="body2" fontWeight={600}>
            {caseDetail.completedTasks}/{caseDetail.totalTasks}
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Overview" />
          <Tab label={`Tasks (${caseDetail.tasks.length})`} />
          <Tab label={`Discrepancies (${caseDetail.discrepancies.length})`} />
          <Tab label={`Activity Logs (${caseDetail.activityLogs.length})`} />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Case Information</Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={4}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Employee:</Typography>
              <Typography variant="body2">{caseDetail.employeeName} ({caseDetail.employeeCode})</Typography>
            </Stack>
            <Stack direction="row" spacing={4}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Assigned To:</Typography>
              <Typography variant="body2">{assignedToDisplayName}</Typography>
            </Stack>
            <Stack direction="row" spacing={4}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Vendor:</Typography>
              <Typography variant="body2">{caseDetail.vendorName || '-'}</Typography>
            </Stack>
            <Stack direction="row" spacing={4}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Created:</Typography>
              <Typography variant="body2">{new Date(caseDetail.createdAt).toLocaleString()}</Typography>
            </Stack>
            {caseDetail.completedAt && (
              <Stack direction="row" spacing={4}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Completed:</Typography>
                <Typography variant="body2">{new Date(caseDetail.completedAt).toLocaleString()}</Typography>
              </Stack>
            )}
            {caseDetail.remarks && (
              <Stack direction="row" spacing={4}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Remarks:</Typography>
                <Typography variant="body2">{caseDetail.remarks}</Typography>
              </Stack>
            )}
          </Stack>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Verification Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {caseDetail.tasks.map((task) => (
                <React.Fragment key={task.id}>
                  <TableRow>
                    <TableCell><Typography fontWeight={600}>{task.verificationTypeName}</Typography></TableCell>
                    <TableCell>
                      <Chip label={BgvTaskStatusLabels[task.status]} color={BgvTaskStatusColors[task.status]} size="small" />
                    </TableCell>
                    <TableCell>{task.assignedToName || task.vendorName || '-'}</TableCell>
                    <TableCell>{task.documents.length} file(s)</TableCell>
                    <TableCell align="center">
                      {canManageBgv ? (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {!isTaskStatusFinalized(task.status) && (
                            <Tooltip title={getVerifyTaskTooltip(task.id)}>
                              <IconButton size="small" color="success"
                                onClick={() => handleTaskStatusUpdate(task.id, BgvTaskStatus.Verified)}>
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!isTaskStatusFinalized(task.status) && (
                            <Tooltip title="Reject">
                              <IconButton size="small" color="error"
                                onClick={() => handleTaskStatusUpdate(task.id, BgvTaskStatus.Rejected)}>
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {task.status === BgvTaskStatus.NotStarted && (
                            <Tooltip title="Start">
                              <Button size="small" variant="outlined"
                                onClick={() => handleTaskStatusUpdate(task.id, BgvTaskStatus.InProgress)}>
                                Start
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title="Upload Document">
                            <IconButton size="small"
                              onClick={() => { setSelectedTaskId(task.id); setUploadDialogOpen(true); }}>
                              <UploadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : isVendorPortal ? (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {task.status === BgvTaskStatus.NotStarted && (
                            <Tooltip title="Start">
                              <Button size="small" variant="outlined"
                                onClick={() => handleTaskStatusUpdate(task.id, BgvTaskStatus.InProgress)}>
                                Start
                              </Button>
                            </Tooltip>
                          )}
                          {(task.status === BgvTaskStatus.InProgress || task.status === BgvTaskStatus.DiscrepancyFound) && (
                            <Tooltip title={getVerifyTaskTooltip(task.id)}>
                              <IconButton size="small" color="success"
                                onClick={() => handleTaskStatusUpdate(task.id, BgvTaskStatus.Verified)}>
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(task.status === BgvTaskStatus.InProgress ||
                            task.status === BgvTaskStatus.DiscrepancyFound) &&
                            canReportTaskDiscrepancy(task) && (
                            <Tooltip title="Report Discrepancy">
                              <IconButton size="small" color="warning"
                                onClick={() => openDiscrepancyDialog(task.id, true)}>
                                <DiscrepancyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Upload Document">
                            <IconButton size="small"
                              onClick={() => { setSelectedTaskId(task.id); setUploadDialogOpen(true); }}>
                              <UploadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Read only</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  {task.documents.map((doc) => (
                    <TableRow key={doc.id} sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ pl: 6 }} colSpan={2}>
                        <Typography variant="body2">{getDocumentDownloadName(doc)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {BgvDocumentStatusLabels[doc.status]} | {new Date(doc.uploadedAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2}>{doc.reviewRemarks || '-'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title={canPreviewFile(getDocumentDownloadName(doc)) ? 'View Attachment' : 'Open Attachment'}>
                            <IconButton size="small" onClick={() => handleOpenDocument(doc)}>
                              <EyeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Attachment">
                            <IconButton size="small" onClick={() => handleDownloadDocument(doc)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canManageBgv && doc.status === BgvDocumentStatus.Uploaded && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton size="small" color="success"
                                  onClick={() => handleDocumentReview(doc.id, BgvDocumentStatus.Approved)}>
                                  <ApproveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton size="small" color="error"
                                  onClick={() => handleDocumentReview(doc.id, BgvDocumentStatus.Rejected)}>
                                  <RejectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {canManageBgv && hasReportableTasks && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<DiscrepancyIcon />}
              onClick={() => openDiscrepancyDialog()}
            >
              Report Discrepancy
            </Button>
          </Box>
        )}
      </TabPanel>

      <Dialog open={previewDialogOpen} onClose={closePreviewDialog} maxWidth="md" fullWidth>
        <DialogTitle>{getPreviewDialogTitle()}</DialogTitle>
        <DialogContent sx={{ height: 500 }}>
          {previewUrl && canRenderPreview ? (
            <iframe
              src={previewUrl}
              title={getPreviewDialogTitle()}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
            />
          ) : (
            <Typography>Preview is not available for this attachment.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {previewUrl && (
            <Button onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}>
              Open in New Tab
            </Button>
          )}
          <Button onClick={closePreviewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>HR Remarks</TableCell>
                <TableCell>Reported</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {caseDetail.discrepancies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No discrepancies found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                caseDetail.discrepancies.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.description}</TableCell>
                    <TableCell>{d.taskName || '-'}</TableCell>
                    <TableCell>
                      <Chip label={BgvDiscrepancySeverityLabels[d.severity]} color={BgvDiscrepancySeverityColors[d.severity]} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={BgvDiscrepancyStatusLabels[d.status]} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{d.hrRemarks || '-'}</TableCell>
                    <TableCell>{new Date(d.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel> */}

      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>HR Remarks</TableCell>
                <TableCell>Reported</TableCell>
                {canManageBgv && <TableCell align="center">Action</TableCell>} {/* ← add */}
              </TableRow>
            </TableHead>
            <TableBody>
              {caseDetail.discrepancies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageBgv ? 7 : 6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No discrepancies found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                caseDetail.discrepancies.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.description}</TableCell>
                    <TableCell>{d.taskName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={BgvDiscrepancySeverityLabels[d.severity]}
                        color={BgvDiscrepancySeverityColors[d.severity]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={BgvDiscrepancyStatusLabels[d.status]}
                        color={d.status === BgvDiscrepancyStatus.Resolved ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{d.hrRemarks || '-'}</TableCell>
                    <TableCell>{new Date(d.createdAt).toLocaleDateString()}</TableCell>
                    {canManageBgv && (
                      <TableCell align="center">
                        {d.status !== BgvDiscrepancyStatus.Resolved ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleResolveDiscrepancy(d.id)}
                          >
                            Mark Resolved
                          </Button>
                        ) : (
                          <Chip label="Resolved" color="success" size="small" />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Performed By</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {caseDetail.activityLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No activity logs</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                caseDetail.activityLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell><Typography fontWeight={600}>{log.action}</Typography></TableCell>
                    <TableCell>{log.details || '-'}</TableCell>
                    <TableCell>{log.performedBy}</TableCell>
                    <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Supported formats: PDF, JPG, PNG
          </Typography>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={discrepancyDialogOpen} onClose={closeDiscrepancyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Report Discrepancy</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {discrepancyError && (
              <Typography color="error" variant="body2">{discrepancyError}</Typography>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>{isDiscrepancyTaskLocked ? 'Task' : 'Task (Optional)'}</InputLabel>
              <Select
                value={discrepancyForm.taskId}
                label={isDiscrepancyTaskLocked ? 'Task' : 'Task (Optional)'}
                disabled={isDiscrepancyTaskLocked || isSubmittingDiscrepancy}
                onChange={(e) => setDiscrepancyForm({ ...discrepancyForm, taskId: e.target.value })}
              >
                {!isDiscrepancyTaskLocked && <MenuItem value="">No specific task</MenuItem>}
                {reportableTasks.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.verificationTypeName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              required
              multiline
              rows={3}
              fullWidth
              value={discrepancyForm.description}
              disabled={isSubmittingDiscrepancy}
              onChange={(e) =>
                setDiscrepancyForm({ ...discrepancyForm, description: e.target.value })
              }
            />
            <TextField
              select
              label="Severity"
              size="small"
              fullWidth
              value={discrepancyForm.severity}
              disabled={isSubmittingDiscrepancy}
              onChange={(e) => setDiscrepancyForm({ ...discrepancyForm, severity: Number(e.target.value) })}
            >
              {Object.entries(BgvDiscrepancySeverityLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Remarks"
              multiline
              rows={2}
              fullWidth
              value={discrepancyForm.hrRemarks}
              disabled={isSubmittingDiscrepancy}
              onChange={(e) => setDiscrepancyForm({ ...discrepancyForm, hrRemarks: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDiscrepancyDialog} disabled={isSubmittingDiscrepancy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateDiscrepancy}
            disabled={!discrepancyForm.description.trim() || (isVendorPortal && !discrepancyForm.taskId) || isSubmittingDiscrepancy}
          >
            {isSubmittingDiscrepancy ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={vendorDecisionDialogOpen} onClose={() => setVendorDecisionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit for HR Review</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Vendor Recommendation"
              fullWidth
              value={selectedDecision}
              onChange={(e) => setSelectedDecision(Number(e.target.value))}
            >
              <MenuItem value={BgvFinalDecision.Clear}>Clear</MenuItem>
              <MenuItem value={BgvFinalDecision.Concern}>Concern</MenuItem>
              <MenuItem value={BgvFinalDecision.NotClear}>Not Clear</MenuItem>
            </TextField>
            <TextField
              label="Remarks"
              multiline
              rows={3}
              fullWidth
              value={decisionRemarks}
              onChange={(e) => setDecisionRemarks(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVendorDecisionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleVendorDecisionSubmit}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={decisionDialogOpen} onClose={() => setDecisionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Final Decision</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {hasOpenDiscrepancies && (
              <Alert severity="error">{decisionBlockedMessage}</Alert>
            )}
            <TextField
              select
              label="Decision"
              fullWidth
              value={selectedDecision}
              onChange={(e) => setSelectedDecision(Number(e.target.value))}
            >
              <MenuItem value={BgvFinalDecision.Clear}>Clear</MenuItem>
              <MenuItem value={BgvFinalDecision.Concern}>Concern</MenuItem>
              <MenuItem value={BgvFinalDecision.NotClear}>Not Clear</MenuItem>
            </TextField>
            <TextField
              label="Remarks"
              multiline
              rows={3}
              fullWidth
              value={decisionRemarks}
              onChange={(e) => setDecisionRemarks(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleFinalDecision} disabled={hasOpenDiscrepancies}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BgvVerificationDetailsPage;
